import { createSelector } from '@reduxjs/toolkit';

import { useAppSelector } from 'src/hooks/useStore';
import { RootState } from 'src/store';
import { arrayToMap } from 'src/utils/array';

export type InvoiceRecipient = {
  id: string;
  firstName?: string;
  lastName?: string;
  companyName?: string;
  email?: string;
  avatarImageUrl?: string;
  fallbackColor?: string;
  type: 'client' | 'company';
  recipient: string;
  entityStatus: string;
};

const selector = createSelector(
  [
    (state: RootState) => state.user.isClient,
    (state: RootState) => state.clients.activeClients,
    (state: RootState) => state.clients.clients,
    (state: RootState) => state.clients.companies,
  ],
  (isClient, activeClients, clientUsers, companies) => ({
    isClient,
    activeClients: arrayToMap(activeClients, 'id'),
    clientUsers: arrayToMap(clientUsers, 'id'),
    companies: arrayToMap(companies, 'id'),
  }),
);

export const useInvoiceRecipient = (
  clientId: string,
  companyId: string,
): InvoiceRecipient => {
  const { isClient, activeClients, clientUsers, companies } =
    useAppSelector(selector);
  const invoiceCompany = companies.get(companyId);

  const isCompanyInvoice = !!invoiceCompany;
  if (isCompanyInvoice && !clientId) {
    const clientCompany = Array.from(
      isClient ? clientUsers : activeClients,
      ([id, entity]) => ({ ...entity, id }),
    ).find((c) => c.fields.companyIds?.includes(companyId));

    return {
      id: companyId,
      companyName: invoiceCompany ? invoiceCompany.fields.name : '',
      recipient: invoiceCompany ? invoiceCompany.fields.name : '',
      firstName: clientCompany ? clientCompany.fields.givenName : '',
      avatarImageUrl: invoiceCompany
        ? invoiceCompany.fields.avatarImageURL
        : '',
      fallbackColor: invoiceCompany ? invoiceCompany.fields.fallbackColor : '',
      type: 'company',
      entityStatus: invoiceCompany ? invoiceCompany.entityStatus : '',
    };
  } else {
    const fetchedClientUser = clientUsers.get(clientId);
    const recipient: InvoiceRecipient = {
      id: clientId,
      firstName: fetchedClientUser ? fetchedClientUser.fields.givenName : '',
      lastName: fetchedClientUser ? fetchedClientUser.fields.familyName : '',
      recipient: `${fetchedClientUser?.fields.givenName} ${fetchedClientUser?.fields.familyName}`,
      email: fetchedClientUser ? fetchedClientUser.fields.email : '',
      avatarImageUrl: fetchedClientUser
        ? fetchedClientUser.fields.avatarImageUrl
        : '',
      fallbackColor: fetchedClientUser
        ? fetchedClientUser.fields.fallbackColor
        : '',
      type: 'client',
      entityStatus: fetchedClientUser ? fetchedClientUser.entityStatus : '',
    };
    if (
      fetchedClientUser?.fields.companyIds &&
      fetchedClientUser.fields.companyIds.length > 1 &&
      companyId
    ) {
      // when client belongs to multiple companies, we need to return the company name this invoice is for
      const company = companies.get(companyId);
      recipient.companyName = company?.fields.name;
    }
    return recipient;
  }
};
